/**
 * components/needs/RequiredDropdown.tsx
 *
 * Pill-style dropdown to toggle a field's required/optional status inline in the top row.
 * "Obligatoire" uses a neutral style with a micro Asterisk icon prefix for visual distinction.
 * "Optionnel" uses a subtle amber tint to signal the exception and draw attention.
 *
 * Created Apr 2026: Replaces the Switch toggle in NeedItemConfigPanel.
 *   Direct click-to-edit pattern aligned with FieldConditionDropdown pill UX.
 * Apr 2026: Added Asterisk icon prefix to Obligatoire for better visual differentiation.
 * Apr 2026 (a11y): Full keyboard navigation - Escape closes, ArrowDown/ArrowUp move focus,
 *   Enter/Space selects. Auto-focuses the active option on open.
 */

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Asterisk } from 'lucide-react';

interface RequiredDropdownProps {
  isRequired: boolean;
  onChange: (isRequired: boolean) => void;
}

const OPTIONS = [
  { value: true, label: 'Obligatoire' },
  { value: false, label: 'Optionnel' },
];

export default function RequiredDropdown({ isRequired, onChange }: RequiredDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      const activeIndex = OPTIONS.findIndex((o) => o.value === isRequired);
      setFocusedIndex(activeIndex);
      optionRefs.current[activeIndex]?.focus();
    } else {
      setFocusedIndex(-1);
    }
  }, [isOpen, isRequired]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (focusedIndex + 1) % OPTIONS.length;
      setFocusedIndex(next);
      optionRefs.current[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (focusedIndex - 1 + OPTIONS.length) % OPTIONS.length;
      setFocusedIndex(prev);
      optionRefs.current[prev]?.focus();
    }
  };

  const pillClass = isRequired
    ? 'bg-neutral-100 text-neutral-700 border-neutral-200 hover:opacity-80'
    : 'bg-amber-50 text-amber-700 border-amber-200 hover:opacity-80';

  return (
    <div ref={ref} className="relative" onKeyDown={handleKeyDown}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${pillClass}`}
      >
        {isRequired && <Asterisk className="w-3 h-3 flex-shrink-0" />}
        <span>{isRequired ? 'Obligatoire' : 'Optionnel'}</span>
        <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1.5 w-36 bg-white rounded-xl shadow-soft-xl border border-neutral-200 py-1 z-20">
          {OPTIONS.map((opt, i) => (
            <button
              key={String(opt.value)}
              ref={(el) => { optionRefs.current[i] = el; }}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setIsOpen(false);
              }}
              className="w-full flex items-center justify-between px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 focus:bg-neutral-50 outline-none transition-colors"
            >
              <span>{opt.label}</span>
              {isRequired === opt.value && <Check className="w-3.5 h-3.5 text-primary-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
